import { useState, useEffect, useRef, useCallback } from "react";
import { auth } from "../firebase.js";

const API_BASE = "https://bluebirddocumentationadmin.pythonanywhere.com";

export function useTaskReference(quillRef, mentionContext) {
  // --- Task Reference state ---
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [highlightIndex, setHighlightIndex] = useState(0);
  const triggerIndexRef = useRef(null);
  const isOpenRef = useRef(false);
  const searchTimeoutRef = useRef(null);

  // --- User Mention state ---
  const [mentionIsOpen, setMentionIsOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [mentionHighlightIndex, setMentionHighlightIndex] = useState(0);
  const mentionTriggerIndexRef = useRef(null);
  const mentionTriggerTextIndexRef = useRef(null);
  const mentionIsOpenRef = useRef(false);
  const mentionSearchTimeoutRef = useRef(null);
  const mentionContextRef = useRef(mentionContext);
  const triggerTextIndexRef = useRef(null);

  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  useEffect(() => { mentionIsOpenRef.current = mentionIsOpen; }, [mentionIsOpen]);
  useEffect(() => { mentionContextRef.current = mentionContext; }, [mentionContext]);

  const searchTasks = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      if (!token) {
        console.log("[TaskRef] No auth token available");
        setResults([]);
        return;
      }
      console.log("[TaskRef] Searching for:", searchQuery);
      const response = await fetch(
        `${API_BASE}/tasks/search/?q=${encodeURIComponent(searchQuery)}`,
        { headers: { Authorization: `${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        console.log("[TaskRef] Search results:", data.length, "tasks");
        setResults(data);
        setHighlightIndex(0);
      } else {
        console.log("[TaskRef] Search failed:", response.status);
      }
    } catch (err) {
      console.error("[TaskRef] Search error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const searchUsers = useCallback(async (searchQuery) => {
    const ctx = mentionContextRef.current;
    setMentionLoading(true);
    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      if (!token) {
        setMentionResults([]);
        return;
      }

      let url;
      let headers = { Authorization: token };

      if (ctx?.mode === "workspace" && ctx.workspaceId) {
        url = `${API_BASE}/workspaces/${ctx.workspaceId}/mention-users/?q=${encodeURIComponent(searchQuery)}`;
        headers = {
          Authorization: token,
          "auth-Token": `Bearer ${token}`,
          "Requesting-User-Uid": user.uid,
        };
      } else if (ctx?.mode === "documentation" && ctx.docType && ctx.docName) {
        url = `${API_BASE}/documentation/mention-users/?type=${encodeURIComponent(ctx.docType)}&name=${encodeURIComponent(ctx.docName)}&q=${encodeURIComponent(searchQuery)}`;
      } else {
        console.log("[UserMention] No valid context:", ctx);
        setMentionResults([]);
        return;
      }

      console.log("[UserMention] Searching:", url);
      const response = await fetch(url, { headers });
      if (response.ok) {
        const data = await response.json();
        console.log("[UserMention] Results:", data.length, "users");
        setMentionResults(data);
        setMentionHighlightIndex(0);
      }
    } catch (err) {
      console.error("[UserMention] Search error:", err);
    } finally {
      setMentionLoading(false);
    }
  }, []);

  const insertTaskReference = useCallback(
    (task) => {
      const editor = quillRef?.current?.getEditor?.();
      if (!editor || triggerIndexRef.current === null) return;

      const triggerIdx = triggerIndexRef.current;
      const currentSelection = editor.getSelection();
      const deleteLength = currentSelection
        ? currentSelection.index - triggerIdx
        : query.length + 1;

      editor.deleteText(triggerIdx, deleteLength, "user");
      editor.insertEmbed(
        triggerIdx,
        "task-reference",
        { boardTaskId: task.board_task_id, boardId: String(task.board_id), taskId: String(task.id) },
        "user"
      );
      editor.insertText(triggerIdx + 1, " ", "user");
      editor.setSelection(triggerIdx + 2, 0, "user");

      setIsOpen(false);
      setQuery("");
      setResults([]);
      triggerIndexRef.current = null;
      triggerTextIndexRef.current = null;
    },
    [quillRef, query]
  );

  const insertUserMention = useCallback(
    (user) => {
      const editor = quillRef?.current?.getEditor?.();
      if (!editor || mentionTriggerIndexRef.current === null) return;

      const triggerIdx = mentionTriggerIndexRef.current;
      const currentSelection = editor.getSelection();
      const deleteLength = currentSelection
        ? currentSelection.index - triggerIdx
        : mentionQuery.length + 1;

      editor.deleteText(triggerIdx, deleteLength, "user");
      editor.insertEmbed(
        triggerIdx,
        "user-mention",
        { uid: user.uid, email: user.email, name: user.name },
        "user"
      );
      editor.insertText(triggerIdx + 1, " ", "user");
      editor.setSelection(triggerIdx + 2, 0, "user");

      setMentionIsOpen(false);
      setMentionQuery("");
      setMentionResults([]);
      mentionTriggerIndexRef.current = null;
      mentionTriggerTextIndexRef.current = null;
    },
    [quillRef, mentionQuery]
  );

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    setResults([]);
    triggerIndexRef.current = null;
    triggerTextIndexRef.current = null;
  }, []);

  const closeMention = useCallback(() => {
    setMentionIsOpen(false);
    setMentionQuery("");
    setMentionResults([]);
    mentionTriggerIndexRef.current = null;
    mentionTriggerTextIndexRef.current = null;
  }, []);

  // Main effect: single set of DOM-level event listeners handling both # and @ triggers
  useEffect(() => {
    let editorRoot = null;
    let editor = null;

    function tryAttach() {
      const ed = quillRef?.current?.getEditor?.();
      if (!ed) return false;
      editor = ed;
      editorRoot = ed.root;
      console.log("[TaskRef] Attaching keyup/input listeners");
      editorRoot.addEventListener("keyup", handleKeyUp);
      editorRoot.addEventListener("input", handleInput);
      return true;
    }

    function openAtPosition(cursorIndex, ed) {
      try {
        const bounds = ed.getBounds(cursorIndex);
        const editorRect = ed.container.getBoundingClientRect();
        return {
          top: editorRect.top + bounds.top + bounds.height + 4,
          left: editorRect.left + bounds.left,
        };
      } catch (e) {
        const editorRect = ed.container.getBoundingClientRect();
        return { top: editorRect.bottom + 4, left: editorRect.left };
      }
    }

    function processChange() {
      if (!editor) return;
      const selection = editor.getSelection();
      if (!selection) return;

      const cursorIndex = selection.index;
      const text = editor.getText(0, cursorIndex);
      // Use text-space indices for character inspection — cursorIndex is a Delta
      // index that counts embedded objects (images, mention blots, etc.) as 1
      // position each, but getText() only returns plain-text characters, so
      // text.length can be less than cursorIndex when embeds are present.
      const lastChar = text.length > 0 ? text[text.length - 1] : undefined;
      const charBefore = text.length > 1 ? text[text.length - 2] : null;

      console.log("[TaskRef] Processing change, cursor at", cursorIndex,
        "last char:", JSON.stringify(lastChar));

      // ── Task reference is open ──
      if (isOpenRef.current && triggerIndexRef.current !== null) {
        const searchText = text.slice(triggerTextIndexRef.current + 1);

        if (cursorIndex <= triggerIndexRef.current) {
          setIsOpen(false); setQuery(""); setResults([]);
          triggerIndexRef.current = null;
          triggerTextIndexRef.current = null;
          return;
        }
        if (searchText === " " || searchText.endsWith(" ")) {
          setIsOpen(false); setQuery(""); setResults([]);
          triggerIndexRef.current = null;
          triggerTextIndexRef.current = null;
          return;
        }

        console.log("[TaskRef] Updating search query:", searchText);
        setQuery(searchText);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => searchTasks(searchText.trim()), 250);
        return;
      }

      // ── User mention is open ──
      if (mentionIsOpenRef.current && mentionTriggerIndexRef.current !== null) {
        const searchText = text.slice(mentionTriggerTextIndexRef.current + 1);

        if (cursorIndex <= mentionTriggerIndexRef.current) {
          setMentionIsOpen(false); setMentionQuery(""); setMentionResults([]);
          mentionTriggerIndexRef.current = null;
          mentionTriggerTextIndexRef.current = null;
          return;
        }
        if (searchText.endsWith(" ") || searchText.includes("\n")) {
          setMentionIsOpen(false); setMentionQuery(""); setMentionResults([]);
          mentionTriggerIndexRef.current = null;
          mentionTriggerTextIndexRef.current = null;
          return;
        }

        console.log("[UserMention] Updating search query:", searchText);
        setMentionQuery(searchText);
        if (mentionSearchTimeoutRef.current) clearTimeout(mentionSearchTimeoutRef.current);
        mentionSearchTimeoutRef.current = setTimeout(() => searchUsers(searchText.trim()), 250);
        return;
      }

      // ── Neither open — check for triggers ──
      if (!lastChar) return;
      const validBefore = charBefore === null || charBefore === "\n" || charBefore === " " || charBefore === "\t";

      if (lastChar === "#" && validBefore) {
        console.log("[TaskRef] # detected, opening dropdown at", cursorIndex - 1);
        triggerIndexRef.current = cursorIndex - 1;
        triggerTextIndexRef.current = text.length - 1;
        setIsOpen(true); setQuery(""); setResults([]); setHighlightIndex(0);
        setPosition(openAtPosition(cursorIndex, editor));
      } else if (lastChar === "@" && validBefore) {
        console.log("[UserMention] @ detected, opening dropdown at", cursorIndex - 1);
        mentionTriggerIndexRef.current = cursorIndex - 1;
        mentionTriggerTextIndexRef.current = text.length - 1;
        setMentionIsOpen(true); setMentionQuery(""); setMentionResults([]); setMentionHighlightIndex(0);
        setMentionPosition(openAtPosition(cursorIndex, editor));
        searchUsers("");
      }
    }

    function handleKeyUp(e) {
      console.log("[TaskRef] keyup:", e.key);
      setTimeout(processChange, 0);
    }

    function handleInput() {
      console.log("[TaskRef] input event");
      setTimeout(processChange, 0);
    }

    if (!tryAttach()) {
      console.log("[TaskRef] Editor not ready, polling...");
      const interval = setInterval(() => {
        if (tryAttach()) clearInterval(interval);
      }, 300);
      return () => clearInterval(interval);
    }

    return () => {
      if (editorRoot) {
        editorRoot.removeEventListener("keyup", handleKeyUp);
        editorRoot.removeEventListener("input", handleInput);
        console.log("[TaskRef] Listeners detached");
      }
    };
  }, [quillRef, searchTasks, searchUsers]);

  // Keyboard navigation for task reference dropdown
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault(); e.stopPropagation();
        setHighlightIndex((prev) => Math.min(prev + 1, results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault(); e.stopPropagation();
        setHighlightIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter" && results.length > 0) {
        e.preventDefault(); e.stopPropagation();
        insertTaskReference(results[highlightIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    };
    const editor = quillRef?.current?.getEditor?.();
    if (!editor) return;
    const editorRoot = editor.root;
    editorRoot.addEventListener("keydown", handleKeyDown, true);
    return () => editorRoot.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen, results, highlightIndex, insertTaskReference, close, quillRef]);

  // Keyboard navigation for user mention dropdown
  useEffect(() => {
    if (!mentionIsOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault(); e.stopPropagation();
        setMentionHighlightIndex((prev) => Math.min(prev + 1, mentionResults.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault(); e.stopPropagation();
        setMentionHighlightIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter" && mentionResults.length > 0) {
        e.preventDefault(); e.stopPropagation();
        insertUserMention(mentionResults[mentionHighlightIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        closeMention();
      }
    };
    const editor = quillRef?.current?.getEditor?.();
    if (!editor) return;
    const editorRoot = editor.root;
    editorRoot.addEventListener("keydown", handleKeyDown, true);
    return () => editorRoot.removeEventListener("keydown", handleKeyDown, true);
  }, [mentionIsOpen, mentionResults, mentionHighlightIndex, insertUserMention, closeMention, quillRef]);

  return {
    taskRefProps: {
      isOpen,
      query,
      results,
      loading,
      position,
      highlightIndex,
      onSelect: insertTaskReference,
      onClose: close,
    },
    userMentionProps: {
      isOpen: mentionIsOpen,
      query: mentionQuery,
      results: mentionResults,
      loading: mentionLoading,
      position: mentionPosition,
      highlightIndex: mentionHighlightIndex,
      onSelect: insertUserMention,
      onClose: closeMention,
    },
  };
}
